import React, { useMemo, useState, useRef } from 'react';
import moment from 'moment';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { v4 as uuid } from 'uuid';
import { useDispatch, useSelector } from 'react-redux';
import StarsIcon from '@mui/icons-material/Stars';
import { Button, Checkbox, FormControlLabel, Typography } from '@mui/material';
import {
  getYoutubeVideoDuration,
  getYoutubeVideoInfo,
} from '@src/services/youtube';
import actions from '@src/redux/actions';
import ProcessHandler from '@src/components/ProcessHandler';
import UpgradeImg from '@src/assets/images/space-shuttle.png';
import ROUTES from '@src/constants/route';
import apis from '@src/apis';
import { ERROR_CODE } from '@src/errors/code';
import NotificationDialog from '@src/components/NotificationDialog';
import NotificationDialogV3 from '@src/components/NotificationDialogV3';
import ActionDialog from '@src/components/Dialog/ActionDialog';
import { createSRTFile } from '@src/services/dubbing';
import {
  CUSTOMER_SUPPORT_PHONE_NUMBER,
  RECAPTCHA_ENTERPRISE_SITE_KEY,
  LANDING_PAGE_URL,
} from '@src/configs';
import useFeatureFlags from '@src/hooks/useFeatureFlags';
import { useCheckDubbingRoute } from '@src/hooks/useCheckDubbingRoute';
import { FEATURE_KEYS } from '@src/configs/featureKeys';
import { checkFeaturePermission } from '@src/services/tts';
import { DUBBING_VIDEO_SOURCE, INPUT_FILE_TYPES } from '@src/constants/dubbing';
import { registerRequestMeta } from '@src/services/moengage';
import { trackTryVoice, TRY_VOICE_ACTION } from '@src/services/voiceTracking';
import { MOE_MODULE, MOE_PAYWALL_TYPE } from '@src/constants/moengage';
import { uploadFileToGoogleStorage } from '@src/services/upload';
import { PACKAGE_STUDIO_VERSION, FEATURE } from '@src/constants/package';
import useUserInfo from '@src/hooks/useTtsUser';
import { useGrowthBook } from '@growthbook/growthbook-react';
import LockCycle from '@src/assets/images/lock-cycle.svg';
import NotificationDialogV4 from '@src/components/NotificationDialogV4';
import TurnstileWidget from '@src/components/TurnstileWidget';
import { DIALOG_V2_TYPE, CAPTCHA_PROVIDER } from '@src/constants';
import { RECAPTCHA_ACTION, RECAPTCHA_TYPE } from '@src/constants/recaptcha';
import { countSubtitleCharacters } from '@src/utils/srt';
import {
  willExceedTrialLimit,
  isVoiceEligibleForTrial,
  buildUpdatedTrialUsage,
} from '@src/services/voiceTrial';
import sanitizeHtml from 'sanitize-html';
import CrownImg from '@src/assets/images/crown.png';
import { checkShouldNotifyLowCredits } from '@src/services/package';
import NotifyLowCredits from '@src/containers/TTSNew/NotifyLowCredits';
import useTotalCredits from '@src/hooks/useTotalCredits';
import { getUpgradePackageLabelKey } from '@src/services/voice';
import Tooltip from '@src/components/Tooltip';
import { StyledUpdateButton, StyledConvertButton } from './index.style';

const ConvertButton = ({
  totalCharacters,
  totalSeconds,
  onChangeLoadingRequest,
  onResetFile,
  onChangeRequestLoading,
  fileSelectedForDubbing,
}) => {
  const { t, i18n } = useTranslation();
  const { language } = i18n;
  const { executeRecaptcha } = useGoogleReCaptcha();
  const growthbook = useGrowthBook();
  const dispatch = useDispatch();
  const history = useHistory();

  const [isLoading, setIsLoading] = useState(false);
  const [openWrongSyntaxDialog, setOpenWrongSyntaxDialog] = useState(null);
  const [actionDialog, setActionDialog] = useState();
  const [openDialogLockedCycle, setOpenDialogLockedCycle] = useState(null);
  const [openWrongSyntaxDialogV2, setOpenWrongSyntaxDialogV2] = useState(null);
  const [charsFactorCheckbox, setCharsFactorCheckbox] = useState(false);
  const [openTooltipCostsCredit, setOpenTooltipCostsCredit] = useState(false);
  const [notifyLowCreditsDialog, setNotifyLowCreditsDialog] = useState(false);
  const { user } = useSelector((state) => state.auth);
  const { infoForDubbingAPI, youtubeLink } = useSelector(
    (state) => state.dubbingRequest,
  );
  const { trialVoiceUsage: stateTrialVoiceUsage } = useSelector(
    (state) => state.user,
  );
  const { getFeatureValue } = useFeatureFlags();

  const isDubbingRoute = useCheckDubbingRoute();

  const isMultipleInputDubbing = getFeatureValue(
    FEATURE_KEYS.MULTIPLE_INPUT_DUBBING,
    { userId: user.id, email: user.email, phoneNumber: user.phoneNumber },
  );

  const voiceTrialConfig = getFeatureValue(FEATURE_KEYS.TRIAL_VOICE_CONFIG, {
    userId: user.id,
    email: user.email,
    phoneNumber: user.phoneNumber,
    packageCode: user.packageCode,
  });
  const enableVoiceTrial = Boolean(voiceTrialConfig);

  const captchaProvider = getFeatureValue(
    FEATURE_KEYS.CAPTCHA_PROVIDER_AIVOICE,
    { userId: user.id, email: user.email },
  );

  const cloudflareTurnstileEnabled = getFeatureValue(
    FEATURE_KEYS.CLOUDFLARE_TURNSTILE_AIVOICE,
    { userId: user.id, email: user.email },
  );

  const enableCreditUsagePreview = getFeatureValue(
    FEATURE_KEYS.ENABLE_CREDIT_USAGE_PREVIEW,
    {
      userId: user.id,
      email: user.email,
      phoneNumber: user.phoneNumber,
    },
  );

  const turnstileWidgetRef = useRef(null);

  const isUsingTurnstile =
    captchaProvider === CAPTCHA_PROVIDER.TURNSTILE &&
    cloudflareTurnstileEnabled;

  const isDubbingExpired =
    user?.dubbing?.packageExpiryDate &&
    moment().isAfter(user?.dubbing?.packageExpiryDate);

  const isStudioExpired =
    user?.packageExpiryDate && moment().isAfter(user.packageExpiryDate);

  const { title, voice, audioType, fileName, sentences, fileId, speed } =
    useSelector((state) => state.dubbingRequest);

  const {
    user: { remainingCharacters, bonusCharacters, dubbing },
  } = useSelector((state) => state.auth);
  const { ttsUser } = useUserInfo();
  const { studio } = ttsUser;
  const { cycle, topUp, custom } = studio || {};

  const { totalCredits } = useTotalCredits();

  let {
    remainingCredits: remainingCycleCredits = 0,
    // eslint-disable-next-line prefer-const
    isLocked: isCycleCreditLocked,
  } = cycle || {};
  let {
    remainingCredits: remainingTopUpCredits = 0,
    // eslint-disable-next-line prefer-const
    isLocked: isTopUpCreditLocked,
  } = topUp || {};

  let {
    remainingCredits: remainingCustomCredits = 0,
    // eslint-disable-next-line prefer-const
    isLocked: isCustomCreditLocked,
  } = custom || {};

  const { usingPackage } = useSelector((state) => state.user);
  const allPackages = useSelector((state) => state.packages.packages);

  const handleUpgradeNow = () =>
    isDubbingRoute
      ? history.push(ROUTES.PAYMENT_DUBBING)
      : history.push(ROUTES.PAYMENT);

  const renderUpgradeButton = () => (
    <StyledUpdateButton onClick={handleUpgradeNow} variant="contained">
      <StarsIcon />
      {t('upgradeNow')}
    </StyledUpdateButton>
  );

  const renderLockedCycleButton = () => (
    <Button variant="contained" onClick={() => setOpenDialogLockedCycle(null)}>
      {t('understoodV3')}
    </Button>
  );

  const getUserPackageFeature = () => {
    const isPackageV2 =
      usingPackage?.version === PACKAGE_STUDIO_VERSION.V2 ||
      usingPackage?.version === PACKAGE_STUDIO_VERSION.V2_1;
    if (isPackageV2) return ttsUser?.features;

    if (isDubbingRoute) {
      return ttsUser?.dubbing?.features;
    }

    return ttsUser?.features;
  };

  const checkVoicePermission = () => {
    if (!voice?.features?.length) return true;
    const { features } = ttsUser;
    return voice.features.every((item) =>
      checkFeaturePermission(features, item),
    );
  };

  const handleOpenVoiceTrialLimitDialog = (limitedVoices) => {
    const voices = (
      Array.isArray(limitedVoices) ? limitedVoices : [limitedVoices]
    ).filter(Boolean);

    if (!voices.length) return;

    const packageName = t(
      getUpgradePackageLabelKey({
        voices,
        userFeatures: ttsUser?.features,
        packages: allPackages,
      }),
    );

    const isMultipleVoices = voices.length > 1;
    const voiceNamesList = voices
      .map((v) => v?.name)
      .filter(Boolean)
      .map((name) => `<li><b>${sanitizeHtml(name)}</b></li>`)
      .join('');

    dispatch(
      actions.limitedFeaturesDialog.showLimitedFeaturesDialog({
        description: isMultipleVoices
          ? t('trialVoiceLimitDescriptionMultiple', {
              voiceNames: voiceNamesList,
              packageName,
            })
          : t('trialVoiceLimitDescription', {
              voiceName: voices[0]?.name || '',
              packageName,
            }),
        img: CrownImg,
        classNameImg: 'custom-img',
        maxWidth: '640px',
        paywall: {
          type: MOE_PAYWALL_TYPE.VOICE_TRIAL_LIMIT,
          voiceCode: voices
            .map((v) => v?.code)
            .filter(Boolean)
            .join(','),
        },
        onUpgrade: () => {
          history.push(ROUTES.PAYMENT);
          dispatch(actions.limitedFeaturesDialog.hideLimitedFeaturesDialog());
        },
      }),
    );
  };

  const checkSubtitlePermission = () => {
    const features = getUserPackageFeature();
    const userFeatures = ttsUser?.features;
    return (
      usingPackage?.id &&
      (features?.includes(FEATURE.SUBTITLE) ||
        userFeatures?.includes(FEATURE.SUBTITLE))
    );
  };

  const validateInputDubbingRequest = () => {
    const haveSubtitleFeature = checkSubtitlePermission();

    const isLockedCycle =
      isCycleCreditLocked &&
      !remainingTopUpCredits &&
      !remainingCharacters &&
      !bonusCharacters;

    const isLockedTopUp =
      isTopUpCreditLocked && !remainingCharacters && !bonusCharacters;

    if (isCycleCreditLocked) remainingCycleCredits = 0;
    if (isTopUpCreditLocked) remainingTopUpCredits = 0;
    if (isCustomCreditLocked) remainingCustomCredits = 0;

    const isExceedCharacters =
      (totalCharacters || 0) >
      remainingCharacters +
        bonusCharacters +
        remainingCycleCredits +
        remainingTopUpCredits +
        remainingCustomCredits;

    const isExceedDuration =
      (totalSeconds || 0) > (dubbing?.remainingSeconds || 0);

    // Function to set dialog for upgrade actions
    const showUpgradeDialog = (titleKey, descriptionKey) => {
      setActionDialog({
        image: UpgradeImg,
        title: t(titleKey),
        description: t(descriptionKey),
        onClose: () => setActionDialog(),
        actionComponents: renderUpgradeButton(),
      });
    };

    // Check if dubbing or studio is expired
    if (isDubbingExpired) {
      if (isStudioExpired) {
        showUpgradeDialog('packageExpired', 'notePackageExpired');
        return false;
      }

      if (!haveSubtitleFeature) {
        showUpgradeDialog('notEnoughDuration', 'noteNotEnoughDuration');
        return false;
      }

      if (isExceedCharacters) {
        showUpgradeDialog('notEnoughCharacters', 'noteNotEnoughCharacters');
        return false;
      }
    } else if (isLockedCycle || isLockedTopUp) {
      setOpenDialogLockedCycle({
        title: t('charactersTemporarilyLocked'),
        description: t('descriptionPopupLockedCredit'),
      });
      return false;
    } else if (isExceedDuration && haveSubtitleFeature && isExceedCharacters) {
      showUpgradeDialog('notEnoughCharacters', 'noteNotEnoughCharacters');
      return false;
    } else if (isExceedDuration && !haveSubtitleFeature) {
      showUpgradeDialog('notEnoughDuration', 'noteNotEnoughDuration');
      return false;
    }

    const canUseVoice = checkVoicePermission();

    // Check trial eligibility: voice is eligible for trial AND user doesn't have permission
    const isTrialEligible = isVoiceEligibleForTrial(voice, voiceTrialConfig);
    const isUsingVoiceTrial =
      enableVoiceTrial && voice && isTrialEligible && !canUseVoice;

    // Check voice trial limit if using trial voice
    if (isUsingVoiceTrial) {
      // Calculate credits for dubbing
      const dubbingCredits = countSubtitleCharacters(sentences, voice) || 0;
      const willExceed = willExceedTrialLimit(
        voice,
        voiceTrialConfig,
        { usage: stateTrialVoiceUsage?.usage || [] },
        dubbingCredits,
        1,
      );

      if (willExceed) {
        handleOpenVoiceTrialLimitDialog([voice]);
        return false;
      }
    }

    // If user doesn't have permission and voice is not eligible for trial
    if (!canUseVoice && !isTrialEligible) {
      dispatch(
        actions.noti.push({
          severity: 'warning',
          message: 'limitedVoiceDescription',
        }),
      );
      return false;
    }

    if (!title)
      dispatch(
        actions.dubbingRequest.updateDubbingRequestByKey('title', fileName),
      );

    if (
      !isDubbingRoute &&
      (totalCharacters || 0) > remainingCharacters + bonusCharacters
    ) {
      showUpgradeDialog('notEnoughCharacters', 'noteNotEnoughCharacters');
      return false;
    }

    const detailErrors = [];
    if (!fileId && !youtubeLink) detailErrors.push(t('mustUploadSubtitleFile'));
    if (!voice) detailErrors.push(t('voiceCannotBeEmpty'));
    if (!audioType) detailErrors.push(t('audioTypeCannotBeEmpty'));
    if (isMultipleInputDubbing) {
      if (!youtubeLink && !fileSelectedForDubbing) {
        detailErrors.push(
          t('uploadFileFormatDubbing', { format: '.srt, .mp4' }),
        );
        detailErrors.push(t('enterYoutubeLinkURL'));
      }

      if (youtubeLink && fileSelectedForDubbing) {
        detailErrors.push(t('onlyOneSource'));
      }
    }

    if (detailErrors.length > 0) {
      setOpenWrongSyntaxDialog({
        name: 'error-syntax',
        variant: 'warning',
        title: t('dubbingRequestInvalid'),
        description: t('dubbingRequestInvalidDescription'),
        subDescription: detailErrors.join('; '),
      });
      return false;
    }
    return true;
  };

  const countCredits = useMemo(() => {
    const countLength = countSubtitleCharacters(sentences, voice);
    return countLength;
  }, [voice, sentences]);

  const getDubbingCredits = () => {
    if (countCredits) return countCredits;
    if (totalCharacters) return totalCharacters;
    if (infoForDubbingAPI?.characters) return infoForDubbingAPI.characters;
    return 0;
  };

  const shouldUpdateTrialUsage = () => {
    if (
      !enableVoiceTrial ||
      !voice ||
      !isVoiceEligibleForTrial(voice, voiceTrialConfig)
    ) {
      return false;
    }
    const canUseCurrentVoice = checkVoicePermission();
    return !canUseCurrentVoice;
  };

  const updateTrialUsageAfterSuccess = (requestId) => {
    if (!shouldUpdateTrialUsage()) return;

    const trialUsageCandidates = [
      {
        voice,
        credits: getDubbingCredits(),
        times: 1,
      },
    ];

    const updatedTrialUsage = buildUpdatedTrialUsage({
      currentUsage: stateTrialVoiceUsage,
      voices: trialUsageCandidates,
      config: voiceTrialConfig,
      userFeatures: ttsUser?.features,
    });

    if (updatedTrialUsage) {
      dispatch(actions.user.updateTrialVoiceUsage(updatedTrialUsage));
    }
    dispatch(
      actions.synthesisRequest.setSynthesizingVoice(
        requestId,
        trialUsageCandidates,
      ),
    );
  };

  const showCharsFactorWarningDialog = () => {
    const generateVoiceSummary = () => {
      const voiceName = voice.name;
      const voiceLevel = voice.level ? t(voice.level) : '';
      const isBeta = voice.beta ? 'Beta' : '';
      // voiceNote combine voiceLevel and isBeta
      const voiceNote = [voiceLevel, isBeta].filter(Boolean).join(' - ');
      const factor = voice.creditFactor || 1;
      const voiceList = t('voiceFactor', {
        voiceName,
        voiceNote: voiceNote ? `(${voiceNote})` : '',
        factor,
      });

      return t('totalFactorCredits', {
        voiceList,
        countCredits: countCredits?.toLocaleString(language),
      });
    };

    setOpenWrongSyntaxDialogV2({
      name: 'error-syntax',
      variant: 'warning',
      title: t('note'),
      description: generateVoiceSummary(),
      type: DIALOG_V2_TYPE.NON_VBEE_WARNING,
    });
  };

  const checkVoiceFactorGreaterThanOne = () => {
    const voiceCredits = voice?.creditFactor || 1;
    if (voiceCredits > 1) return true;
    return false;
  };

  const handleConvertSubtitle = async (
    sentencesConvert = sentences,
    isShowCharsFactorDialog = true,
  ) => {
    const notShowCharsFactorDialog = localStorage.getItem('charsFactorDialog');
    if (
      isShowCharsFactorDialog &&
      !notShowCharsFactorDialog &&
      checkVoiceFactorGreaterThanOne()
    ) {
      showCharsFactorWarningDialog();
      return;
    }

    const isValidRequest = validateInputDubbingRequest();
    if (!isValidRequest) return;
    setIsLoading(true);
    const { error, fileUrl } = await createSRTFile(
      `${fileName}.srt`,
      sentencesConvert,
    );
    if (error) {
      dispatch(
        actions.noti.push({
          message: t('systemError', { hotline: CUSTOMER_SUPPORT_PHONE_NUMBER }),
          severity: 'error',
        }),
      );
      setIsLoading(false);
      return;
    }

    const options = {};

    if (captchaProvider === CAPTCHA_PROVIDER.RECAPTCHA) {
      let recaptchaToken;
      const recaptchaTtsType = growthbook.getFeatureValue(
        FEATURE_KEYS.RECAPTCHA_TTS_TYPE,
      );
      switch (recaptchaTtsType) {
        case RECAPTCHA_TYPE.V3:
          if (executeRecaptcha) {
            recaptchaToken = await executeRecaptcha(RECAPTCHA_ACTION.SYNTHESIS);
          }
          break;
        case RECAPTCHA_TYPE.ENTERPRISE:
          if (window.grecaptcha) {
            recaptchaToken = await window?.grecaptcha?.enterprise?.execute(
              RECAPTCHA_ENTERPRISE_SITE_KEY,
            );
          }
          break;
        default:
          break;
      }
      if (recaptchaToken) options.recaptchaToken = recaptchaToken;
    }

    if (isUsingTurnstile) {
      options.turnstileToken = await turnstileWidgetRef.current.onVerify();
      turnstileWidgetRef.current.reset();
    }

    const response = await apis.dubbing.createDubbingRequest(
      {
        title: title || fileName,
        subtitleLink: fileUrl,
        voiceCode: voice?.code,
        audioType,
        speed,
      },
      options,
    );

    onChangeLoadingRequest(true);
    onChangeRequestLoading(true);
    setIsLoading(false);
    if (response?.status === 1) {
      onResetFile();
      dispatch(
        actions.dubbingRequest.resetDubbingRequest([
          'title',
          'fileId',
          'error',
          'fileName',
          'sentences',
        ]),
      );
      registerRequestMeta(response?.result?.requestId, {
        module: MOE_MODULE.SRT,
        voice,
      });
      trackTryVoice({ voice, action: TRY_VOICE_ACTION.CONVERT });
      updateTrialUsageAfterSuccess(response?.result?.requestId);
      const isNotifyLowCredits = checkShouldNotifyLowCredits({
        beforeCredits: totalCredits,
        afterCredits: totalCredits - (totalCharacters || 0),
        packageCode: user.packageCode,
      });
      if (isNotifyLowCredits) setNotifyLowCreditsDialog(true);
    } else {
      switch (response?.errorCode) {
        case ERROR_CODE.SPAM_ACCOUNT:
        case ERROR_CODE.EXCEED_RATE_LIMIT:
          setOpenWrongSyntaxDialogV2({
            name: 'error-syntax',
            variant: 'warning',
            title: t('cannotConvertDubbing'),
            description: t('blockSpamDubbingDesc'),
            type: DIALOG_V2_TYPE.BLOCK_SYNTHESIS,
          });
          break;
        case ERROR_CODE.VOICE_TRIAL_LIMIT_REACHED:
          if (enableVoiceTrial) {
            const responseTrial = await apis.user.getTrialVoiceUsage();
            const latestTrialUsage =
              responseTrial?.result?.voiceUsage || stateTrialVoiceUsage;
            dispatch(actions.user.updateTrialVoiceUsage(latestTrialUsage));
            handleOpenVoiceTrialLimitDialog([voice]);
          }
          break;
        default:
          dispatch(
            actions.noti.push({
              message: t(response?.errorMessage || 'systemError', {
                hotline: CUSTOMER_SUPPORT_PHONE_NUMBER,
              }),
              severity: 'error',
            }),
          );
          break;
      }
    }
  };

  const createDubbingRequestWithYoutubeLink = async () => {
    setIsLoading(true);
    dispatch(
      actions.dubbingRequest.updateDubbingRequestByKey('isConverting', true),
    );

    let videoDuration;
    let videoTitle;

    if (!infoForDubbingAPI.title) {
      const { isVideoNotFound, data: videoInfo } = await getYoutubeVideoInfo(
        youtubeLink,
      );
      if (isVideoNotFound) {
        dispatch(
          actions.noti.push({
            message: t('videoNotFound'),
            severity: 'error',
          }),
        );
        setIsLoading(false);
        dispatch(
          actions.dubbingRequest.updateDubbingRequestByKey(
            'isConverting',
            false,
          ),
        );
        return;
      }
      videoTitle = videoInfo?.title;
    }

    if (!infoForDubbingAPI?.videoDuration) {
      videoDuration = await getYoutubeVideoDuration(youtubeLink);
    }

    const response = await apis.dubbing.createDubbingRequestWithFileVideo({
      title: infoForDubbingAPI?.title || fileName || videoTitle,
      linkVideo: youtubeLink,
      voiceCode: voice?.code,
      audioType,
      speed,
      source: DUBBING_VIDEO_SOURCE.YOUTUBE,
      videoDuration: infoForDubbingAPI?.videoDuration || videoDuration,
    });
    onChangeLoadingRequest(true);
    onChangeRequestLoading(true);

    if (response?.status === 1) {
      onResetFile();
      dispatch(
        actions.dubbingRequest.resetDubbingRequest([
          'title',
          'fileId',
          'error',
          'fileName',
          'sentences',
          'youtubeLink',
          'infoForDubbingAPI',
          'isConverting',
        ]),
      );
      registerRequestMeta(response?.result?.requestId, {
        module: MOE_MODULE.SRT,
        voice,
      });
      updateTrialUsageAfterSuccess();
    } else {
      dispatch(
        actions.noti.push({
          message: t(response?.errorMessage || 'systemError', {
            hotline: CUSTOMER_SUPPORT_PHONE_NUMBER,
          }),
          severity: 'error',
        }),
      );
    }
    setIsLoading(false);
    dispatch(
      actions.dubbingRequest.updateDubbingRequestByKey('isConverting', false),
    );
  };

  const createDubbingRequestWithLocalVideo = async () => {
    setIsLoading(true);
    dispatch(
      actions.dubbingRequest.updateDubbingRequestByKey('isConverting', true),
    );
    const extension = fileSelectedForDubbing.name.split('.').pop();
    let name = fileSelectedForDubbing.name.split('.')[0];
    // remove special character like [ ] ( ) . , - _ and replace space by -
    name = name.replace(/[^a-zA-Z0-9]/g, '');
    const directory = 'videos';
    const fileLocation = await uploadFileToGoogleStorage(
      name,
      directory,
      extension,
      fileSelectedForDubbing,
    );

    const response = await apis.dubbing.createDubbingRequestWithFileVideo({
      title: fileName,
      linkVideo: fileLocation,
      voiceCode: voice?.code,
      audioType,
      speed,
      source: DUBBING_VIDEO_SOURCE.LOCAL,
      videoDuration: infoForDubbingAPI?.videoDuration,
    });
    onChangeLoadingRequest(true);
    onChangeRequestLoading(true);

    if (response?.status === 1) {
      onResetFile();
      dispatch(
        actions.dubbingRequest.resetDubbingRequest([
          'title',
          'fileId',
          'error',
          'fileName',
          'sentences',
          'youtubeLink',
          'infoForDubbingAPI',
          'isConverting',
        ]),
      );
      registerRequestMeta(response?.result?.requestId, {
        module: MOE_MODULE.SRT,
        voice,
      });
      updateTrialUsageAfterSuccess();
    } else {
      dispatch(
        actions.noti.push({
          message: t(response?.errorMessage || 'systemError', {
            hotline: CUSTOMER_SUPPORT_PHONE_NUMBER,
          }),
          severity: 'error',
        }),
      );
    }
    setIsLoading(false);
    dispatch(
      actions.dubbingRequest.updateDubbingRequestByKey('isConverting', false),
    );
  };

  const handleConvertSubtitleWithVideo = async () => {
    const isValidRequest = validateInputDubbingRequest();
    if (!isValidRequest) return;

    if (Object.keys(sentences).length > 0) {
      handleConvertSubtitle();
      return;
    }

    if (
      infoForDubbingAPI?.fileType === INPUT_FILE_TYPES.SRT &&
      Object.keys(infoForDubbingAPI?.sentences).length > 0
    ) {
      const subtitleSentences = infoForDubbingAPI?.sentences.reduce(
        (allSentences, currBlock) => {
          const sentenceId = uuid();
          // eslint-disable-next-line no-param-reassign
          allSentences[sentenceId] = currBlock;
          return allSentences;
        },
        {},
      );
      handleConvertSubtitle(subtitleSentences);
      return;
    }

    if (youtubeLink.length > 0) {
      await createDubbingRequestWithYoutubeLink();
      return;
    }

    if (fileSelectedForDubbing) {
      await createDubbingRequestWithLocalVideo();
    }
  };

  const handleClickCharsFactorCheckbox = () => {
    if (!charsFactorCheckbox) {
      localStorage.setItem('nonVbeeDialog', true);
      localStorage.setItem('charsFactorDialog', true);
    } else {
      localStorage.removeItem('nonVbeeDialog');
      localStorage.removeItem('charsFactorDialog');
    }
    setCharsFactorCheckbox((value) => !value);
  };

  const handleConfirmNonVbeeDialog = async () => {
    setOpenWrongSyntaxDialogV2(null);
    await handleConvertSubtitle(sentences, false);
  };

  const renderActionPopupCharsFactor = () => (
    <div className="non-vbee-warning">
      <FormControlLabel
        control={
          <Checkbox
            classes={{ root: 'checkbox' }}
            color="iconPrimary"
            value={charsFactorCheckbox}
            onChange={handleClickCharsFactorCheckbox}
          />
        }
        label={
          <Typography className="not-show-again-text">
            {t('notShowAgain')}
          </Typography>
        }
        labelPlacement={t('notShowAgain')}
      />
      <div className="btn-group">
        <Button
          className="btn-non-vbee-warning cancel-btn"
          variant="outlined"
          onClick={() => setOpenWrongSyntaxDialogV2(null)}
        >
          {t('cancel')}
        </Button>
        <Button
          className="btn-non-vbee-warning"
          variant="contained"
          onClick={handleConfirmNonVbeeDialog}
        >
          {t('convert')}
        </Button>
      </div>
    </div>
  );

  const renderCloseButton = (handleClose, showBuyPackage = false) => {
    if (openWrongSyntaxDialogV2?.type === DIALOG_V2_TYPE.NON_VBEE_WARNING) {
      return renderActionPopupCharsFactor();
    }

    if (openWrongSyntaxDialogV2?.type === DIALOG_V2_TYPE.BLOCK_SYNTHESIS) {
      return (
        <Button
          className="btn-understood"
          variant="contained"
          onClick={() => {
            window.open(`${LANDING_PAGE_URL}/support`, '_blank');
          }}
        >
          {t('submitSupportRequest')}
        </Button>
      );
    }

    if (showBuyPackage) {
      return (
        <Button
          className="btn-understood"
          variant="contained"
          onClick={() => history.push(ROUTES.PAYMENT)}
        >
          {t('buyPackages')}
        </Button>
      );
    }

    return (
      <Button
        className="btn-understood"
        variant="contained"
        onClick={handleClose}
      >
        {t('understood')}
      </Button>
    );
  };

  return (
    <>
      <Tooltip
        arrow
        placement="top"
        open={enableCreditUsagePreview && !isLoading && openTooltipCostsCredit}
        disableHoverListener={!enableCreditUsagePreview || isLoading}
        title={t('costsCredit', {
          value: countCredits?.toLocaleString(language),
        })}
        onMouseEnter={() => setOpenTooltipCostsCredit(true)}
        onMouseLeave={() => setOpenTooltipCostsCredit(false)}
      >
        <StyledConvertButton
          className="convert-button"
          color="primary"
          variant="contained"
          onClick={() => {
            if (isMultipleInputDubbing) {
              handleConvertSubtitleWithVideo();
            } else {
              handleConvertSubtitle();
            }
          }}
          disabled={isLoading}
        >
          <ProcessHandler
            loading={isLoading}
            ml="46px"
            mr="46px"
            size={24}
            align="center"
            color="divider"
          >
            {t('convertSubtitle')}
          </ProcessHandler>
        </StyledConvertButton>
      </Tooltip>

      <NotificationDialog
        name={openWrongSyntaxDialog?.name}
        title={openWrongSyntaxDialog?.title}
        description={openWrongSyntaxDialog?.description}
        subDescription={openWrongSyntaxDialog?.subDescription}
        variant="warning"
        open={!!openWrongSyntaxDialog?.title}
        onClose={() => setOpenWrongSyntaxDialog(null)}
        actionComponent={renderCloseButton(() =>
          setOpenWrongSyntaxDialog(null),
        )}
      />
      <ActionDialog
        image={actionDialog?.image}
        open={!!actionDialog?.title}
        title={actionDialog?.title}
        description={actionDialog?.description}
        onClose={actionDialog?.onClose}
        actionComponents={actionDialog?.actionComponents}
      />

      {!!openDialogLockedCycle?.title && (
        <NotificationDialogV4
          image={LockCycle}
          title={openDialogLockedCycle?.title}
          description={openDialogLockedCycle?.description}
          actionComponent={renderLockedCycleButton()}
          open={!!openDialogLockedCycle?.title}
          onClose={() => setOpenDialogLockedCycle(null)}
        />
      )}

      {!!openWrongSyntaxDialogV2?.title && (
        <NotificationDialogV3
          name={openWrongSyntaxDialogV2?.name}
          title={openWrongSyntaxDialogV2?.title}
          description={openWrongSyntaxDialogV2?.description}
          subDescription={openWrongSyntaxDialogV2?.subDescription}
          variant={openWrongSyntaxDialogV2?.variant}
          open={!!openWrongSyntaxDialogV2?.title}
          onClose={() => setOpenWrongSyntaxDialogV2(null)}
          actionComponent={renderCloseButton(
            () => setOpenWrongSyntaxDialogV2(null),
            true,
          )}
        />
      )}

      {isUsingTurnstile && <TurnstileWidget ref={turnstileWidgetRef} />}

      {notifyLowCreditsDialog && (
        <NotifyLowCredits
          open={notifyLowCreditsDialog}
          onClose={() => setNotifyLowCreditsDialog(false)}
          usingPackage={usingPackage}
        />
      )}
    </>
  );
};

export default ConvertButton;
